import type React from "react";
import moment from "moment";
import {IconButton, Stack, Tooltip, Typography} from "@mui/material";
import RedoIcon from "~/icons/svg/RedoIcon.svg?react";
import UndoIcon from "~/icons/svg/UndoIcon.svg?react";
import {Tag} from "~/components/tag/Tag";
import type {TagColor} from "~/components/tag/tag.styles";
import {PageHeader} from "~/components/main/header/PageHeader.tsx";
import {ApprovalStatusEnum, type TaxonomyApprovalRequest} from "~/services/classes/AiClasses";

const STATUS_COLOR: Partial<Record<ApprovalStatusEnum, TagColor>> = {
    [ApprovalStatusEnum.PENDING]: 'warning',
    [ApprovalStatusEnum.APPROVED]: 'success',
    [ApprovalStatusEnum.REJECTED]: 'error',
    [ApprovalStatusEnum.ERROR]: 'error',
};

type ApprovalReviewHeaderProps = {
    request?: TaxonomyApprovalRequest;
    onBack: () => void;
    /** Undefined hides the button (nothing to undo / redo, or read only). */
    onUndo?: () => void;
    onRedo?: () => void;
};

/** Back to the approvals, the title and status (in the top bar), who sent it and when, and undo / redo of the approver's edits. */
export const ApprovalReviewHeader: React.FC<ApprovalReviewHeaderProps> = ({request, onBack, onUndo, onRedo}) => {
    const status = request?.current_status.status;
    const author = request && (`${request.created_by.first_name} ${request.created_by.last_name}`.trim() || request.created_by.email);

    return (
        <Stack direction="row" alignItems="center" gap={1}>
            {/* Back control, title and status live in the app's top bar (Mithra 3.1 header) */}
            <PageHeader
                back={{label: 'Back to approvals', onClick: onBack}}
                title="Review taxonomy changes"
                tag={request && status &&
                    <Tag label={request.current_status.status_name} color={STATUS_COLOR[status] ?? 'default'} size="small"/>}
            />
            <Stack sx={{flex: 1, minWidth: 0}}>
                {request && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                        Sent by {author} · {moment(request.created_timestamp).format('L LT')}
                    </Typography>
                )}
            </Stack>
            {onUndo && (
                <Tooltip title="Undo">
                    <IconButton size="small" aria-label="Undo" onClick={onUndo}><UndoIcon width={20} height={20}/></IconButton>
                </Tooltip>
            )}
            {onRedo && (
                <Tooltip title="Redo">
                    <IconButton size="small" aria-label="Redo" onClick={onRedo}><RedoIcon width={20} height={20}/></IconButton>
                </Tooltip>
            )}
        </Stack>
    );
};
